import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Save, Edit3, User, Mail, Building, Plus, Trash2, Eye } from 'lucide-react';
import ImagePreviewModal from './ImagePreviewModal';
import { FORM_FIELD_TYPES } from '../config';
import { findFormDataKey, getApplicableFields, getExtraFormDataKeys } from '../utils/formSchemaFields';

const UserDetailsEditor = ({ 
  userData, 
  formSchema = [], 
  onSave, 
  onCancel,
  onChange,
  isLoading = false,
  currentScanPurpose = null, // QR scan purpose to check for blacklisted fields
  startInEditMode = false,
  className = "" 
}) => {
  const [formData, setFormData] = useState({});
  const [isEditing, setIsEditing] = useState(startInEditMode);
  const [hasChanges, setHasChanges] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (userData?.formData) {
      setFormData(userData.formData);
    }
  }, [userData]);

  const resolveKey = (fieldLabel) => findFormDataKey(formData, fieldLabel);

  const getFieldValue = (fieldLabel) => {
    const raw = formData[resolveKey(fieldLabel)];
    if (raw === null || raw === undefined) return '';
    if (typeof raw === 'object') return ''; // files/objects handled by the file renderer
    return raw;
  };

  const getFieldOptions = (field) => {
    if (Array.isArray(field.options) && field.options.length) return field.options;
    if (Array.isArray(field.optionsWithPricing) && field.optionsWithPricing.length) {
      return field.optionsWithPricing.map((o) => o.label);
    }
    if (Array.isArray(field.optionWithBlackLists) && field.optionWithBlackLists.length) {
      return field.optionWithBlackLists.map((o) => o.label);
    }
    return [];
  };

  const handleFieldChange = (fieldLabel, value) => handleKeyChange(resolveKey(fieldLabel), value);

  const handleKeyChange = (key, value) => {
    const newFormData = {
      ...formData,
      [key]: value
    };
    setFormData(newFormData);
    setHasChanges(true);
    
    // Call onChange prop for live updates
    if (onChange) {
      onChange(newFormData);
    }
  };

  const handleSave = () => {
    if (onSave) {
      onSave(formData);
    }
    setIsEditing(false);
    setHasChanges(false);
  };

  const handleCancel = () => {
    if (userData?.formData) {
      setFormData(userData.formData);
    }
    setIsEditing(false);
    setHasChanges(false);
    if (onCancel) {
      onCancel();
    }
  };

  const renderListEditor = (id, items, disabled, onItemsChange) => (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex gap-2">
          <Input
            id={index === 0 ? id : undefined}
            value={item ?? ''}
            disabled={disabled}
            className={disabled ? 'bg-gray-50' : ''}
            onChange={(e) => onItemsChange(items.map((v, i) => (i === index ? e.target.value : v)))}
          />
          {!disabled && (
            <Button type="button" variant="outline" size="sm" onClick={() => onItemsChange(items.filter((_, i) => i !== index))}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      ))}
      {!disabled && (
        <Button type="button" variant="outline" size="sm" onClick={() => onItemsChange([...items, ''])}>
          <Plus className="w-4 h-4 mr-1" />
          Add
        </Button>
      )}
      {disabled && items.length === 0 && <p className="text-sm text-gray-500">No items</p>}
    </div>
  );

  // Values outside the schema: primitives, string lists, and one level of nested objects are editable.
  const renderExtraField = (key) => {
    const value = formData[key];
    const disabled = !isEditing;
    const inputClass = disabled ? 'bg-gray-50' : '';
    let control;
    if (Array.isArray(value) && value.every((v) => v == null || typeof v !== 'object')) {
      control = renderListEditor(key, value, disabled, (items) => handleKeyChange(key, items));
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      control = (
        <div className="space-y-2 border-l-2 border-gray-100 pl-3">
          {Object.entries(value).map(([childKey, childValue]) => (
            <div key={childKey} className="space-y-1">
              <Label className="text-xs text-gray-600">{childKey}</Label>
              {childValue !== null && typeof childValue === 'object' ? (
                <p className="text-sm text-gray-700 break-all">{JSON.stringify(childValue)}</p>
              ) : (
                <Input
                  value={childValue ?? ''}
                  disabled={disabled}
                  className={inputClass}
                  onChange={(e) => handleKeyChange(key, { ...value, [childKey]: e.target.value })}
                />
              )}
            </div>
          ))}
        </div>
      );
    } else if (Array.isArray(value)) {
      control = <p className="text-sm text-gray-700 break-all">{JSON.stringify(value)}</p>;
    } else {
      control = (
        <Input
          id={key}
          value={value ?? ''}
          disabled={disabled}
          className={inputClass}
          onChange={(e) => handleKeyChange(key, e.target.value)}
        />
      );
    }
    return (
      <div key={key} className="space-y-2">
        <Label htmlFor={key} className="text-sm font-medium">{key}</Label>
        {control}
      </div>
    );
  };

  const renderField = (field) => {
    const fieldConfig = FORM_FIELD_TYPES[field.type]
      || (field.type === 'radio' ? FORM_FIELD_TYPES.dropdown : FORM_FIELD_TYPES.text);

    const value = getFieldValue(field.label);
    
    // Check if this field is blacklisted at field level (using backend field name)
    let isBlacklisted = currentScanPurpose && 
      field.blackedListedQrPurposes && 
      field.blackedListedQrPurposes.includes(currentScanPurpose);
    
    // For dropdown/select fields, also check option-level blacklisting
    if (!isBlacklisted && (field.type === 'dropdown' || field.type === 'radio') && value) {
      const optionWithBlacklists = field.optionWithBlackLists || field.optionsWithPricing || [];
      const selectedOption = optionWithBlacklists.find(opt => opt.label === value);
      if (selectedOption && selectedOption.blackListedQrPurposes) {
        isBlacklisted = currentScanPurpose && selectedOption.blackListedQrPurposes.includes(currentScanPurpose);
      }
    }
    
    // Field is disabled if not editing OR if blacklisted
    const isFieldDisabled = !isEditing || isBlacklisted;

    if (fieldConfig.component === 'list') {
      const raw = formData[resolveKey(field.label)];
      const items = Array.isArray(raw) ? raw : (raw ? [String(raw)] : []);
      return (
        <div key={field.label} className="space-y-2">
          <Label htmlFor={field.label} className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {renderListEditor(field.label, items, isFieldDisabled, (next) => handleFieldChange(field.label, next))}
        </div>
      );
    }

    if (fieldConfig.component === 'input') {
      return (
        <div key={field.label} className="space-y-2">
          <Label htmlFor={field.label} className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
            {isBlacklisted && (
              <span className="ml-2 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded">
                Locked for this QR purpose
              </span>
            )}
          </Label>
          <Input
            id={field.label}
            type={fieldConfig.inputType}
            value={value}
            onChange={(e) => handleFieldChange(field.label, e.target.value)}
            placeholder={field.placeholder}
            disabled={isFieldDisabled}
            className={isFieldDisabled ? 'bg-gray-50' : ''}
          />
        </div>
      );
    }

    if (fieldConfig.component === 'textarea') {
      return (
        <div key={field.label} className="space-y-2">
          <Label htmlFor={field.label} className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
            {isBlacklisted && (
              <span className="ml-2 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded">
                Locked for this QR purpose
              </span>
            )}
          </Label>
          <Textarea
            id={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.label, e.target.value)}
            placeholder={field.placeholder}
            disabled={isFieldDisabled}
            className={isFieldDisabled ? 'bg-gray-50' : ''}
            rows={3}
          />
        </div>
      );
    }

    if (fieldConfig.component === 'select') {
      return (
        <div key={field.label} className="space-y-2">
          <Label htmlFor={field.label} className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
            {isBlacklisted && (
              <span className="ml-2 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded">
                Locked for this QR purpose
              </span>
            )}
          </Label>
          <Select
            value={value}
            onValueChange={(newValue) => handleFieldChange(field.label, newValue)}
            disabled={isFieldDisabled}
          >
            <SelectTrigger className={isFieldDisabled ? 'bg-gray-50' : ''}>
              <SelectValue placeholder={field.placeholder || `Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              {(() => {
                const options = getFieldOptions(field);
                // Always include the currently-stored value so it renders even if
                // it's no longer part of the configured options.
                const allOptions = value && !options.includes(value) ? [...options, value] : options;
                return allOptions.filter((option) => option && String(option).trim() !== '').map((option, index) => (
                  <SelectItem key={index} value={option}>
                    {option}
                  </SelectItem>
                ));
              })()}
            </SelectContent>
          </Select>
        </div>
      );
    }

    if (fieldConfig.component === 'file') {
      return (
        <div key={field.label} className="space-y-2">
          <Label htmlFor={field.label} className="text-sm font-medium">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
            {isBlacklisted && (
              <span className="ml-2 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded">
                Locked for this QR purpose
              </span>
            )}
          </Label>
          <div className={`border-2 border-dashed rounded-lg p-4 text-center ${
            isBlacklisted ? 'border-gray-200 bg-gray-50' : 'border-gray-300'
          }`}>
            {value ? (
              <div className="space-y-2">
                {userData?.files?.[value] ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 text-sm text-blue-600"
                    onClick={() => setPreview(userData.files[value])}
                  >
                    {/^(jpe?g|png|gif|webp|bmp)$/i.test(userData.files[value].format || '') && (
                      <img src={userData.files[value].url} alt="" className="h-10 w-10 rounded object-cover" />
                    )}
                    <Eye className="h-4 w-4" />
                    View
                  </button>
                ) : (
                  <p className="text-sm text-gray-600">Current file: {value}</p>
                )}
                {isEditing && !isBlacklisted && (
                  <input
                    type="file"
                    accept={field.accept}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleFieldChange(field.label, file.name);
                      }
                    }}
                    className="text-sm"
                  />
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-gray-500">No file uploaded</p>
                {isEditing && !isBlacklisted && (
                  <input
                    type="file"
                    accept={field.accept}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleFieldChange(field.label, file.name);
                      }
                    }}
                    className="text-sm"
                  />
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  const applicableFields = getApplicableFields(formSchema, formData);
  const extraKeys = getExtraFormDataKeys(formData, applicableFields);

  if (!userData) {
    return (
      <Card className={className}>
        <CardContent className="p-8 text-center">
          <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No User Data</h3>
          <p className="text-gray-500">Scan a QR code to load user details for editing.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <User className="w-5 h-5 text-blue-600" />
            <CardTitle className="text-lg">User Details</CardTitle>
          </div>
          <div className="flex items-center space-x-2">
            {!isEditing ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(true)}
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Edit
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={isLoading || !hasChanges}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* User Info Summary */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <User className="w-4 h-4 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">User ID</p>
                <p className="font-medium">{userData.userId}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium">{userData.formData?.['Email'] || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Building className="w-4 h-4 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <p className="font-medium capitalize">{userData.status || 'Unknown'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Editable Form Fields */}
        <div className="space-y-4">
          <h3 className="text-md font-medium text-gray-900">Registration Details</h3>
          {formSchema.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {applicableFields.map(renderField)}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No form schema available for this user.</p>
            </div>
          )}
        </div>

        {extraKeys.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-md font-medium text-gray-900">Other Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {extraKeys.map(renderExtraField)}
            </div>
          </div>
        )}

        {/* Changes Indicator */}
        {hasChanges && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <Edit3 className="w-4 h-4 text-yellow-600" />
              <p className="text-sm text-yellow-800">
                You have unsaved changes. Click "Save Changes" to update the user's information.
              </p>
            </div>
          </div>
        )}
      </CardContent>
      {preview && (
        <ImagePreviewModal
          imageUrl={preview.url}
          kind={/pdf/i.test(preview.format || '') || /\.pdf(\?|$)/i.test(preview.fileName || '') ? 'pdf' : 'image'}
          onClose={() => setPreview(null)}
        />
      )}
    </Card>
  );
};

export default UserDetailsEditor;
